// DEPRECATED: Prefer using the useTeamMembers() hook from app/src/hooks/useTeamMembers.ts directly.
// This context is kept for backward compatibility.

import React, { createContext, useContext, ReactNode } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { teamService } from "@/services/teamService";

export interface TeamMember {
  id: string;
  name: string;
  email: string;
  phone: string;
  role: string;
  role_id?: string;
  org_unit: string;
  title: string;
  avatar: string;
  bio: string;
  joinDate: string;
  status: "active" | "inactive";
}

interface TeamContextType {
  members: TeamMember[];
  isLoading: boolean;
  updateMember: (id: string, updates: Partial<TeamMember>) => void;
  getMembersByOrgUnit: (org_unit: string) => TeamMember[];
}

const TeamContext = createContext<TeamContextType | undefined>(undefined);

export function TeamProvider({ children }: { children: ReactNode }) {
  const { hasTenant } = useAuth() as { hasTenant: boolean };
  const queryClient = useQueryClient();

  const { data: members = [], isLoading } = useQuery({
    queryKey: ["team"],
    queryFn: teamService.list,
    enabled: hasTenant,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["team"] });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<TeamMember> }) =>
      teamService.update(id, data),
    onSuccess: invalidate,
  });

  const updateMember = (id: string, updates: Partial<TeamMember>) =>
    updateMutation.mutate({ id, data: updates });

  const getMembersByOrgUnit = (org_unit: string) =>
    members.filter(
      (m) => m.org_unit.toLowerCase() === org_unit.toLowerCase() && m.status === "active"
    );

  return (
    <TeamContext.Provider
      value={{ members, isLoading, updateMember, getMembersByOrgUnit }}
    >
      {children}
    </TeamContext.Provider>
  );
}

export function useTeam() {
  const context = useContext(TeamContext);
  if (!context) {
    throw new Error("useTeam must be used within a TeamProvider");
  }
  return context;
}
